<template>
  <div>
    <v-container v-if="step === 0">
      <v-layout wrap row>
        <v-flex xs8 primary--text>
          <p class="display-1 my-3">Student Accounts</p>
          <p class="subheading my-3">Your license includes access for all your students. Let's get them setup.</p>
          <p class="text-xs-right my-5">
            <v-btn large v-on:click="step++" color="secondary">
              Start
            </v-btn>
          </p>
        </v-flex>
        <v-flex xs4>
          &nbsp;
        </v-flex>
      </v-layout>
    </v-container>
    <v-container v-if="step === 1">
      <v-layout wrap row>
        <v-flex xs8 primary--text>
          <p class="display-1 my-3">Shared Accounts</p>
          <p class="subheading my-3">Students share generic accounts (they will <strong>not</strong> have individual accounts). This makes student setup incredibly easy and eliminates privacy concerns.</p>
          <p class="text-xs-right my-5">
            <v-btn large v-on:click="step++" color="secondary">
              Next
            </v-btn>
          </p>
        </v-flex>
        <v-flex xs4 px-4>
          <img src="@/assets/shared_icon.png" style="width:100%; max-width: 200px"/>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container v-if="step === 2">
      <v-layout wrap row>
        <v-flex xs12 primary--text>
          <p class="display-1 my-3">Scope</p>
          <p class="subheading my-3">As your school's license administrator, you have the unique ability to create a Schoolwide generic account for all students to share. Other teachers will see Schoolwide accounts but only you can create and edit them. You can also create a generic account for only your students as normal teacher accounts would.</p>
        </v-flex>
        <v-flex xs12 my-3>
          <v-layout justify-center>
            <v-flex xs5 text-xs-center mx-3 pa-3 elevation-3 class="float-box">
              <p><img src="@/assets/schoolwide_icon.png" style="width:100%; max-width: 64px"/></p>
              <p class="subheading my-3" style="font-weight: bold;">Schoolwide</p>
              <p class="subheading my-3">Generic account for all students in the school. All teachers will see this.</p>
              <p class="text-xs-center">
                <v-btn v-on:click="createSchoolwide = true; step++" color="secondary">
                  Select
                </v-btn>
              </p>
            </v-flex>
            <v-flex xs5 text-xs-center mx-3 pa-3 elevation-3 class="float-box">
              <p><img src="@/assets/classwide_icon.png" style="width:100%; max-width: 64px"/></p>
              <p class="subheading my-3" style="font-weight: bold;">Just My Students</p>
              <p class="subheading my-3">Generic account for only your students. Other teachers will not see this.</p>
              <p class="text-xs-center">
                <v-btn v-on:click="createSchoolwide = false; step++" color="secondary">
                  Select
                </v-btn>
              </p>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container v-if="step === 3">
      <v-layout>
        <v-flex xs8 primary--text>
          <p class="display-1 my-3">Username &amp; Password</p>
          <p class="subheading my-3 mb-5">Choose an easy-to-remember Username and Password for your students to use. For example, if your last name is Dexter and you teach class 3A, you might choose Dexter3A for both the Username and Password.</p>
          <form v-on:keyup.enter="createNewClass">
            <v-layout row wrap>
              <v-flex xs12 v-if="error" mb-3>
                <v-alert
                  :value="error"
                  type="error"
                  outline
                >
                  {{ error }}
                </v-alert>
              </v-flex>
              <v-flex xs8>
                <v-text-field
                  color="secondary"
                  label="Username"
                  hint="Usernames must be universally unique."
                  v-model="username"
                  :rules="[rules.required]"
                  outline
                ></v-text-field>
              </v-flex>
              <v-flex xs8>
                <v-text-field
                  color="secondary"
                  label="Password"
                  hint="Passwords are case-sensitive."
                  v-model="password"
                  :rules="[rules.required]"
                  outline
                ></v-text-field>
              </v-flex>
            </v-layout>
          </form>
          <p class="text-xs-right my-5">
            <v-btn
              large
              v-if="!loading"
              color="secondary"
              v-on:click="createNewClass">Submit</v-btn>
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="secondary"
            ></v-progress-circular>
          </p>
        </v-flex>
        <v-flex xs4 px-4>
          <img src="@/assets/creds_icon.png" style="width:100%; max-width: 200px"/>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container v-if="step === 4">
      <v-layout wrap row>
        <v-flex xs8 primary--text>
          <p class="display-1 my-3">Generic Account Created!</p>
          <p class="subheading my-3">Students can start using the generic account by:</p>
          <p class="subheading my-3">
            <ol>
              <li>Going to <a style="color: #3296f3" href="https://www.brainingcamp.com" target="_blank">www.brainingcamp.com</a></li>
              <li>Clicking Sign In</li>
              <li>
                <p>Entering the account details:</p>
                <p class="my-3">Username: <strong>{{ this.username }}</strong><br/>Password: <strong>{{ this.password }}</strong></p>
              </li>
            </ol>
          </p>
          <p class="text-xs-right my-5">
            <v-btn large v-on:click="complete()" color="secondary">
              Finish
            </v-btn>
          </p>
        </v-flex>
        <v-flex xs4 px-4>
          <img src="@/assets/class_success.png" style="width:100%; max-width: 400px" class="elevation-5"/>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import ClassroomService from '@/services/ClassroomService'

export default {
  components: {
  },
  data: function () {
    return {
      step: 0,
      loading: false,
      createSchoolwide: false,
      username: null,
      password: null,
      error: null,
      rules: {
          required: value => !!value || 'Required.'
      }
    }
  },
  mounted: function () {
  },
  methods: {
    async createNewClass () {
      if (this.loading) return;
      const credentials = {
        username: this.username,
        password: this.password
      };

      this.loading = true
      if (this.createSchoolwide) {
        try {
          const result = await ClassroomService.createSchoolwideClassroom(credentials);
          
          if (result) {
            this.step = 4;
            this.error = null;
          }
        } catch (err) {
          this.error = err.response.data.error;
        }
      } else {
        try {
          const result = await ClassroomService.post(credentials);
          
          if (result) {
            this.step = 4;
            this.error = null;
          }
        } catch (err) {
          this.error = err.response.data.error;
        }
      }
      this.loading = false;
    },
    complete () {
      this.$router.push({
        path: '/account/manage-students'
      })
    }
  }
}
</script>

<style scoped>
button {
  margin: 0;
}

li {
  margin: 1em;
}

.float-box {
  border: 1px solid #cecece;
  border-radius: 16px;
}
</style>
